import { useState } from 'react'
import type { LossLabels } from '@/pages/landing/types'

// Copied from RoiCalculator rather than extracted into a shared file: that component is
// kept intact and reactivable (spec 2026-07-29, decision 2), and extracting would mean
// editing it.
function Slider({ label, value, onChange, min, max, step = 1, display, bound }: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
  // Overrides the figure shown next to the label — the loss slider states a rate and the
  // count it works out to, so its own value isn't the whole story.
  display?: string
  bound?: (v: number) => string
}) {
  const format = bound ?? String

  return (
    <div>
      <label className="flex items-center justify-between mb-2.5">
        <span className="font-bold text-[0.92rem] text-paper">{label}</span>
        <span className="font-extrabold text-mustard text-[1.25rem] tabular-nums">{display ?? value}</span>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer bg-paper/20 accent-mustard"
      />
      <div className="flex justify-between text-xs text-paper/40 mt-1.5 tabular-nums">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  )
}

// Middle of the "5-10 % of kegs lost per year" figure the Problem block already claims.
const DEFAULT_LOSS_RATE = 7

export default function LossCalculator({ labels, kegValue, kegCount, onKegCountChange }: {
  labels: LossLabels
  kegValue: number
  kegCount: number
  onKegCountChange: (value: number) => void
}) {
  // A rate rather than a count of kegs, so both sliders always move the result: state the
  // fleet and the number of lost kegs follows, state the rate and the euros follow. A raw
  // count would have made the fleet slider change nothing on screen — which reads as a
  // broken control — and would have needed a "has the visitor touched it yet" flag to fake
  // the link. The rate is what the brewer's intuition actually holds ("we lose a few
  // percent a year"); the count is derived, and shown so the rate stays concrete.
  const [lossRate, setLossRate] = useState(DEFAULT_LOSS_RATE)

  const lostPerYear = Math.round((kegCount * lossRate) / 100)
  const annualLoss = lostPerYear * kegValue
  const monthlyLoss = Math.round(annualLoss / 12)
  const unit = lostPerYear > 1 ? labels.unit_other : labels.unit_one

  return (
    <section id="calculator" className="py-[76px] px-[22px]">
      <div className="max-w-[1080px] mx-auto">
        <div className="max-w-[46ch] mx-auto text-center mb-11">
          <span className="font-script text-flame text-xl inline-block -rotate-1">{labels.eyebrow}</span>
          <h2 className="font-body font-extrabold text-ink text-3xl md:text-4xl tracking-tight mt-1">
            {labels.title} <span className="sketch">{labels.highlight}</span>
          </h2>
          <p className="text-ink-soft text-lg mt-3">{labels.subtitle}</p>
        </div>

        <div className="max-w-[680px] mx-auto rounded-[24px] bg-ink border-2 border-ink shadow-soft overflow-hidden">
          <div className="p-8 lg:p-9">
            <h3 className="font-body font-extrabold text-xl text-paper mb-1">{labels.panel_title}</h3>
            <p className="text-paper/70 text-[0.95rem] mb-6">{labels.panel_sub}</p>

            <div className="space-y-6">
              <Slider
                label={labels.slider_count}
                value={kegCount}
                onChange={onKegCountChange}
                min={10}
                max={800}
                step={10}
              />
              {/* 0-15 %: the scale brackets the 5-10 % the Problem block claims, close enough
                  that the default sits mid-scale and wide enough to cover a bad year. A wider
                  range would let a visitor dial in a figure no brewery would recognise. */}
              <Slider
                label={labels.slider_lost}
                value={lossRate}
                onChange={setLossRate}
                min={0}
                max={15}
                display={`${lossRate} % · ${lostPerYear} ${unit}`}
                bound={(v) => `${v} %`}
              />
            </div>
          </div>

          <div className="bg-card border-t-2 border-ink p-8 lg:p-9">
            <div className="grid grid-cols-2 gap-4 text-center mb-6">
              <div>
                <div className="text-[0.68rem] font-extrabold text-ink-soft uppercase tracking-wider mb-1.5">{labels.res_year}</div>
                <div className="font-body font-extrabold text-[2.75rem] leading-none text-flame tabular-nums">
                  {annualLoss.toLocaleString()}€
                </div>
              </div>
              <div>
                <div className="text-[0.68rem] font-extrabold text-ink-soft uppercase tracking-wider mb-1.5">{labels.res_month}</div>
                <div className="font-body font-extrabold text-[2.75rem] leading-none text-ink tabular-nums">
                  {monthlyLoss.toLocaleString()}€
                </div>
              </div>
            </div>

            <p className="text-center text-ink-soft border-t-2 border-dashed border-line pt-5">{labels.conclusion}</p>
            <p className="text-center text-xs text-ink-faint mt-2">
              {labels.assumption.replace('%{price}', String(kegValue))}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
